import Vue from "vue";
import VueRouter from "vue-router";

import { Role } from "@/acl/roles";
import { ProductVisibility } from "@/assets/constants/permissions";
import config from "@/config";
import { setUseOperationsWarehouse } from "@/http/useOperationsWarehouse";
import { i18n } from "@/main.js";
import store from "@/store/store.js";

import { embeddedProductsRoutes, productsReportGenerateRoutes, productsRoutes } from "./products";
import {
  beforeEnterChangeLocale,
  beforeEnterHoldingDataAdjustments,
  buildPathWithLang,
} from "./utils";

Vue.use(VueRouter);

// =============================================================================
// HOME ROUTES
// =============================================================================

let mainLayoutRoutes = [];

// Set the home page based on what's configured for this org (table vs standard home page).
if (config.homePageTable) {
  mainLayoutRoutes.push({
    path: buildPathWithLang("/"),
    name: "home",
    component: () => import("../views/HomeTable.vue"),
    meta: {
      authorize: config.allowedRoles,
      allowAdminOfOneProduct: true,
    },
    beforeEnter: beforeEnterChangeLocale,
  });
} else {
  mainLayoutRoutes.push({
    path: buildPathWithLang("/"),
    name: "home",
    component: () => import("../views/Home.vue"),
    meta: {
      authorize: config.allowedRoles,
      allowAdminOfOneProduct: true,
    },
    beforeEnter: beforeEnterChangeLocale,
  });
}

mainLayoutRoutes.push(...productsRoutes);

// If the platform was built with langInUrl, but somebody tries to access the home page without
// a lang, we redirect to the home page with a lang in the url.
if (config.langInUrl) {
  mainLayoutRoutes.push({
    path: "",
    redirect: `/${config.availableLanguages[0]}`,
  });
}

// =============================================================================
// HELP ROUTES
// =============================================================================

// Add the help routes we configured.
if (config.help) {
  if (config.help.faq) {
    mainLayoutRoutes.push({
      path: buildPathWithLang("/help/faq"),
      name: "help_faq",
      component: () => import("../views/help/Faq.vue"),
      meta: {
        authorize: config.allowedRoles,
        allowAdminOfOneProduct: true,
      },
      beforeEnter: beforeEnterChangeLocale,
    });
  }

  if (config.help.knowledgeBase) {
    mainLayoutRoutes = mainLayoutRoutes.concat([
      {
        path: buildPathWithLang("/help/knowledge-base"),
        name: "help_knowledgeBase",
        component: () => import("../views/help/KnowledgeBase.vue"),
        meta: {
          authorize: config.allowedRoles,
          allowAdminOfOneProduct: true,
        },
        beforeEnter: beforeEnterChangeLocale,
      },
      {
        path: buildPathWithLang("/help/knowledge-base/terms/:term"),
        name: "help_knowledgeBase_term",
        component: () => import("../views/help/knowledgeBaseTerm/KnowledgeBaseTerm.vue"),
        meta: {
          authorize: config.allowedRoles,
          allowAdminOfOneProduct: true,
        },
        beforeEnter: beforeEnterChangeLocale,
        props: true,
      },
    ]);
  }

  if (config.help.contact) {
    mainLayoutRoutes.push({
      path: buildPathWithLang("/help/contact"),
      name: "help_contact",
      component: () => import("../views/help/Contact.vue"),
      meta: {
        authorize: config.allowedRoles,
        allowAdminOfOneProduct: true,
      },
      beforeEnter: beforeEnterChangeLocale,
    });
  }
}

// =============================================================================
// ADMIN ROUTES
// =============================================================================

mainLayoutRoutes.push({
  path: buildPathWithLang("/admin/users"),
  name: "admin_users",
  component: () => import("../views/admin/Users.vue"),
  meta: {
    authorize: [Role.admin, Role.superAdmin],
  },
  beforeEnter: beforeEnterChangeLocale,
});
mainLayoutRoutes.push({
  path: buildPathWithLang("/admin/products"),
  name: "admin_products",
  component: () => import("../views/admin/products/Products.vue"),
  meta: {
    authorize: [Role.admin, Role.superAdmin],
    allowAdminOfOneProduct: true,
  },
  beforeEnter: beforeEnterChangeLocale,
});
mainLayoutRoutes.push({
  path: buildPathWithLang("/admin/platform-settings"),
  name: "admin_platformSettings",
  component: () => import("../views/admin/PlatformSettings.vue"),
  meta: {
    authorize: [Role.admin, Role.superAdmin],
  },
  beforeEnter: beforeEnterChangeLocale,
});
mainLayoutRoutes.push({
  path: buildPathWithLang("/admin/operations/generate-reports"),
  name: "admin_operations_generateReports",
  component: () => import("../views/admin/operations/GenerateReports.vue"),
  meta: {
    authorize: [Role.admin, Role.superAdmin],
    allowAdminOfOneProduct: true,
  },
  beforeEnter: beforeEnterChangeLocale,
});
mainLayoutRoutes.push({
  path: buildPathWithLang("/admin/operations/update-bulk-components"),
  name: "admin_operations_bulkComponentUpdate",
  component: () => import("../views/admin/operations/BulkComponentUpdate.vue"),
  meta: {
    authorize: [Role.admin, Role.superAdmin],
  },
  beforeEnter: beforeEnterChangeLocale,
});

const hasUpdatablePriceData = config.products.some((product) => product.allowPriceDataModification);
if (hasUpdatablePriceData) {
  mainLayoutRoutes.push({
    path: buildPathWithLang("/admin/operations/bulk-price-data-update"),
    name: "admin_operations_bulkPriceDataUpdate",
    component: () => import("../views/admin/operations/BulkPriceDataUpdate.vue"),
    meta: {
      authorize: [Role.admin, Role.superAdmin],
    },
    beforeEnter: beforeEnterChangeLocale,
  });
}
mainLayoutRoutes.push({
  path: buildPathWithLang("/admin/operations/export-platform"),
  name: "admin_operations_exportPlatform",
  component: () => import("../views/admin/operations/Export.vue"),
  meta: {
    authorize: [Role.admin, Role.superAdmin],
  },
  beforeEnter: beforeEnterChangeLocale,
});

mainLayoutRoutes.push({
  path: buildPathWithLang("/admin/holding-data-adjustments/categorise"),
  name: "admin_holdingDataAdjustments_categorise",
  component: () => import("../views/admin/holdingDataAdjustments/Categorise.vue"),
  meta: {
    authorize: [Role.admin, Role.superAdmin],
    allowAdminOfOneProduct: true,
  },
  beforeEnter: beforeEnterHoldingDataAdjustments,
});
mainLayoutRoutes.push({
  path: buildPathWithLang("/admin/holding-data-adjustments/translate"),
  name: "admin_holdingDataAdjustments_translate",
  component: () => import("../views/admin/holdingDataAdjustments/Translate.vue"),
  meta: {
    authorize: [Role.admin, Role.superAdmin],
    allowAdminOfOneProduct: true,
  },
  beforeEnter: beforeEnterHoldingDataAdjustments,
});
mainLayoutRoutes.push({
  path: buildPathWithLang("/admin/holding-data-adjustments/override"),
  name: "admin_holdingDataAdjustments_override",
  component: () => import("../views/admin/holdingDataAdjustments/Override.vue"),
  meta: {
    authorize: [Role.admin, Role.superAdmin],
    allowAdminOfOneProduct: true,
  },
  beforeEnter: beforeEnterHoldingDataAdjustments,
});

// =============================================================================
// SETTINGS ROUTES
// =============================================================================

mainLayoutRoutes.push({
  path: buildPathWithLang("/settings/oveview"),
  name: "settings_overview",
  component: () => import("../views/settings/Settings.vue"),
  meta: {
    authorize: [Role.insight, Role.team, Role.admin, Role.superAdmin],
    noNeedOfInvestorProfile: true,
  },
  beforeEnter: beforeEnterChangeLocale,
  props: {
    sectionId: "overview",
  },
});
mainLayoutRoutes.push({
  path: buildPathWithLang("/settings/edit-profile"),
  name: "settings_editProfile",
  component: () => import("../views/settings/Settings.vue"),
  meta: {
    authorize: [Role.insight, Role.team, Role.admin, Role.superAdmin],
    noNeedOfInvestorProfile: true,
  },
  beforeEnter: beforeEnterChangeLocale,
  props: (route) => ({
    sectionId: "editProfile",
    askVerification: route.query?.askVerification ? true : false,
  }),
});
mainLayoutRoutes.push({
  path: buildPathWithLang("/settings/change-password"),
  name: "settings_changePassword",
  component: () => import("../views/settings/Settings.vue"),
  meta: {
    authorize: [Role.insight, Role.team, Role.admin, Role.superAdmin],
    noNeedOfInvestorProfile: true,
  },
  beforeEnter: beforeEnterChangeLocale,
  props: {
    sectionId: "changePassword",
  },
});
mainLayoutRoutes.push({
  path: buildPathWithLang("/settings/delete-account"),
  name: "settings_deleteAccount",
  component: () => import("../views/settings/Settings.vue"),
  meta: {
    authorize: [Role.insight, Role.team, Role.admin, Role.superAdmin],
    noNeedOfInvestorProfile: true,
  },
  beforeEnter: beforeEnterChangeLocale,
  props: {
    sectionId: "deleteAccount",
  },
});

// Some deprecated URLs may still be referenced externally (e.g. from old links). To handle these
// gracefully, we can define redirections from outdated URLs to valid ones.
if (config.urlRedirections != undefined) {
  const fromUrlToToUrl = {};
  for (const { fromUrl, toUrl } of config.urlRedirections) {
    fromUrlToToUrl[buildPathWithLang(fromUrl)] = buildPathWithLang(toUrl);
  }

  // If a redirection source matches an existing route, attach the redirect directly to that route
  // definition and remove it from the remaining mapping.
  for (const mainLayoutRoute of mainLayoutRoutes) {
    if (mainLayoutRoute.path in fromUrlToToUrl) {
      mainLayoutRoute.redirect = fromUrlToToUrl[mainLayoutRoute.path];
      delete fromUrlToToUrl[mainLayoutRoute.path];
    }
  }

  // For redirections where the source route no longer exists (or never existed), create new routes
  // that redirect to the correct targets.
  for (const [fromUrl, toUrl] of Object.entries(fromUrlToToUrl)) {
    mainLayoutRoutes.push({
      path: fromUrl,
      redirect: toUrl,
      beforeEnter: beforeEnterChangeLocale,
    });
  }
}

// =============================================================================
// INVESTMENT PROPOSAL ROUTES
// =============================================================================

const investmentProposalRoutes = config.investmentProposal
  ? [
      {
        path: "",
        component: () => import("@/layouts/main/Main.vue"),
        children: [
          {
            path: buildPathWithLang("/investment-proposal/weight-optimization"),
            name: "investmentProposal_weightOptimization",
            component: () => import("@/views/investmentProposal/WeightOptimization.vue"),
            meta: { authorize: config.investmentProposal.allowedRoles },
            beforeEnter: beforeEnterChangeLocale,
          },
          {
            path: buildPathWithLang("/investment-proposal/report-generation"),
            name: "investmentProposal_reportGeneration",
            component: () =>
              import("@/views/investmentProposal/reportGeneration/ReportGeneration.vue"),
            meta: { authorize: config.investmentProposal.allowedRoles },
            beforeEnter: beforeEnterChangeLocale,
          },
        ],
      },
      {
        path: "",
        component: () => import("@/layouts/full-page/FullPageBasic.vue"),
        children: [
          {
            path: buildPathWithLang("/investment-proposal/report-for-pdf-generation"),
            name: "investmentProposal_reportForPdfGeneration",
            component: () =>
              import("@/views/investmentProposal/reportGeneration/ReportGenerationForPdfGeneration.vue"),
            meta: {
              noNeedOfInvestorProfile: true,
              authorize: config.investmentProposal.allowedRoles,
            },
            beforeEnter: beforeEnterChangeLocale,
          },
        ],
      },
    ]
  : [];

const routes = [
  {
    // =============================================================================
    // MAIN LAYOUT ROUTES
    // =============================================================================
    path: "",
    component: () => import("../layouts/main/Main.vue"),
    children: mainLayoutRoutes,
  },
  // =============================================================================
  // PAGES
  // =============================================================================
  {
    // =============================================================================
    // Report pages to generate the pdfs
    // =============================================================================
    path: "",
    component: () => import("@/layouts/full-page/FullPageBasic.vue"),
    children: productsReportGenerateRoutes,
  },
  // =============================================================================
  // Investment proposal pages
  // =============================================================================
  ...investmentProposalRoutes,
  // =============================================================================
  // Custom sections pages
  // =============================================================================
  ...(config.customSections?.routes ?? []),
  {
    path: "",
    component: () => import("@/layouts/full-page/FullPage.vue"),
    children: [
      // =============================================================================
      // Auth Routes
      // =============================================================================
      {
        path: buildPathWithLang("/auth/login"),
        name: "page-login",
        component: () => import("@/views/auth/login/Login.vue"),
        meta: {
          authorize: [Role.guest],
          noNeedOfInvestorProfile: true,
        },
        beforeEnter: beforeEnterChangeLocale,
        props: true,
      },
      {
        path: buildPathWithLang("/auth/login-2fa"),
        name: "page-login-2fa",
        component: () => import("@/views/auth/login/Login2fa.vue"),
        meta: {
          authorize: [Role.guest],
          noNeedOfInvestorProfile: true,
        },
        beforeEnter: beforeEnterChangeLocale,
        props: true,
      },
      {
        path: buildPathWithLang("/auth/register"),
        name: "page-register",
        component: () => import("@/views/auth/register/Register.vue"),
        meta: {
          authorize: [Role.guest],
          noNeedOfInvestorProfile: true,
        },
        beforeEnter: beforeEnterChangeLocale,
      },
      {
        path: buildPathWithLang("/auth/register-2fa"),
        name: "page-register-2fa",
        component: () => import("@/views/auth/register/Register2fa.vue"),
        meta: {
          authorize: [Role.insight],
          noNeedOfInvestorProfile: true,
        },
        beforeEnter: beforeEnterChangeLocale,
      },
      {
        // Note that we don't use 'buildPathWithLang' because this link is agnostic of language.
        path: "/auth/handled-by-firebase",
        beforeEnter(to) {
          // We want to keep the query params and add them to the Firebase URL.
          const allQueryParams = Object.keys(to.query).map((key) => `${key}=${to.query[key]}`);
          const queryParamsStr = allQueryParams.join("&");
          // Put the full page url including the protocol http(s) below
          const authDomain = process.env.VUE_APP_FIREBASE_AUTH_DOMAIN;
          window.location = `https://${authDomain}/__/auth/action?${queryParamsStr}`;
        },
        meta: {
          noNeedOfInvestorProfile: true,
        },
      },
      {
        path: buildPathWithLang("/auth/reset-password-form"),
        name: "reset-password-form",
        component: () => import("@/views/auth/resetPassword/ResetPasswordForm.vue"),
        meta: {
          authorize: [Role.guest],
          noNeedOfInvestorProfile: true,
        },
        beforeEnter: beforeEnterChangeLocale,
      },
      // =============================================================================
      // Compliance Routes
      // =============================================================================
      {
        path: buildPathWithLang("/compliance/terms-of-use"),
        name: "compliance_termsofuse",
        component: () => import("@/views/compliance/TermsOfUse.vue"),
        meta: {
          noNeedOfInvestorProfile: true,
        },
        beforeEnter: beforeEnterChangeLocale,
      },
      {
        path: buildPathWithLang("/compliance/privacy-policy"),
        name: "compliance_privacypolicy",
        component: () =>
          config.customPrivacyPolicyPath
            ? import(`@/views/${config.customPrivacyPolicyPath}`)
            : import("@/views/compliance/PrivacyPolicy.vue"),
        meta: {
          noNeedOfInvestorProfile: true,
        },
        beforeEnter: beforeEnterChangeLocale,
      },
      {
        path: buildPathWithLang("/compliance/ombudsman"),
        name: "compliance_ombudsman",
        component: () => import("@/views/compliance/Ombudsman.vue"),
        meta: {
          noNeedOfInvestorProfile: true,
        },
        beforeEnter: beforeEnterChangeLocale,
      },
      {
        path: buildPathWithLang("/investor-profile"),
        name: "page-investor-profile",
        component: () => import("@/views/compliance/InvestorProfile.vue"),
        meta: {
          noNeedOfInvestorProfile: true,
        },
        beforeEnter: beforeEnterChangeLocale,
      },
      {
        path: buildPathWithLang("/unauthorized-investor-profile"),
        name: "page-unauthorized-investor-profile",
        component: () => import("@/views/errors/UnauthorizedInvestorProfile.vue"),
        meta: {
          noNeedOfInvestorProfile: true,
        },
        beforeEnter: beforeEnterChangeLocale,
        props: true,
      },

      // =============================================================================
      // Error Routes
      // =============================================================================
      {
        path: buildPathWithLang("/error-404"),
        name: "page-error-404",
        component: () => import("@/views/errors/Error404.vue"),
        meta: {
          noNeedOfInvestorProfile: true,
        },
        beforeEnter: beforeEnterChangeLocale,
      },
      {
        path: buildPathWithLang("/error-403"),
        name: "page-error-403",
        component: () => import("@/views/errors/Error403.vue"),
        meta: {
          noNeedOfInvestorProfile: true,
        },
        beforeEnter: beforeEnterChangeLocale,
        props: true,
      },
    ],
  },
  {
    // =============================================================================
    // EMBEDDED ROUTES
    // =============================================================================
    path: "",
    component: () => import("../layouts/embedded/Embedded.vue"),
    children: embeddedProductsRoutes,
  },
  // Since we now don't have the server at the same URL as the client, let's redirect any
  // requests that are using the old URL, as some people maybe have old links.
  {
    path: "/api/*",
    beforeEnter(to) {
      // We want to keep the query params and add them to the Firebase URL.
      const allQueryParams = Object.keys(to.query).map((key) => `${key}=${to.query[key]}`);
      const queryParamsStr = allQueryParams.join("&");
      window.location = `${process.env.VUE_APP_API_URL}${to.path}?${queryParamsStr}`;
    },
    meta: {
      noNeedOfInvestorProfile: true,
    },
  },
  {
    path: "*",
    redirect: "/error-404",
    meta: {
      noNeedOfInvestorProfile: true,
    },
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  },
});

router.onReady(() => {
  // If there is `embed=true` in the parameters, we set this info in 'sessionStorage'.
  // This is useful to know, in some components, if we need to display things slightly differently
  // (e.g. not displaying the language dropdown when embedded and on mobile for PPT).
  if (router.currentRoute.query.embed === "true") {
    sessionStorage.setItem("isEmbedded", "true");
  }
});

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById("loading-bg");
  if (appLoading) {
    appLoading.style.display = "none";
  }
});

router.beforeEach(async (to, from, next) => {
  // The first element we check is if the page is accessible to the user.
  // If they try to access something they shouldn't have access too, they
  // will be redirected to a 403.
  const { authorize, allowAdminOfOneProduct, allowAdminOfOneShareClass, productId } = to.meta;
  // The document manager (Puppeteer) sends a custom
  // token to sign in from a headless browser.
  if (to.query.customFirebaseTokenForPuppeteer) {
    // Log in with the token passed in.
    await store.dispatch(
      "auth/signInWithCustomToken",
      to.query.customFirebaseTokenForPuppeteer.toString()
    );
  }

  // Batch report generation uses the operations Snowflake warehouse for API reads.
  setUseOperationsWarehouse(to.query.useOperationsWarehouse === "true");

  await store.dispatch("userInfo/setAndGetUserRoles");

  // If 2FA is enabled and the user is logged in but hasn't registered 2FA,
  // redirect them to the 2FA registration page.
  if (
    config.enable2FactorAuthentication &&
    store.getters["userInfo/hasAnyRole"](Role.insight) &&
    !store.getters["userInfo/hasRegistered2fa"]
  ) {
    // If the user is already navigating to the 2FA registration page, allow it.
    // Otherwise, redirect them to the 2FA registration page.
    if (to.name === "page-register-2fa") {
      return next();
    } else {
      return next({
        name: "page-register-2fa",
        params: { lang: i18n.locale },
      });
    }
  }

  // If a logged in user tries to reach the login or register page,
  // they are redirected to the home page.
  if (
    (["page-login", "page-register", "page-login-2fa", "page-register-2fa"].includes(to.name) &&
      !store.getters["userInfo/hasAnyRoles"](authorize)) ||
    (to.name === "page-register-2fa" && store.getters["userInfo/hasAnyRole"](Role.insight))
  ) {
    return next({ name: "home", params: to.params });
  }

  if (authorize && !store.getters["userInfo/hasAnyRoles"](authorize)) {
    // First check if the user cannot access any product because of their role.
    // Go to 403 page if that's the case.
    const authorizedProductsForRole = store.getters["allProducts/getAllProductsAuthorizedForRole"];
    if (authorizedProductsForRole.length === 0) {
      return next({
        name: "page-error-403",
        params: {
          // It looks weird, but actually the route that we wanted to go to was `to`, but
          // we redirect to the 403 page, so the route we redirected from is indeed the `to`.
          redirectedFromPath: to.path,
          lang: i18n.locale,
        },
      });
    }

    // If not authorized by basic roles, check if we allowed when the user
    // is an admin of at least one product.
    const isAdminOfOne = config.products.some((product) =>
      store.getters["userInfo/isAdminOf"](product.productId)
    );
    if (!allowAdminOfOneProduct || !isAdminOfOne) {
      // If not even admin for the current product, check if the user is an admin of
      // at least one share class of the product.
      if (allowAdminOfOneShareClass && productId) {
        const shareClassesAdmin = store.getters["allProducts/getAllShareClassesAdmin"](productId);
        if (!shareClassesAdmin || shareClassesAdmin.length === 0) {
          return next({ name: "page-error-403", params: { lang: i18n.locale } });
        }
      } else {
        return next({ name: "page-error-403", params: { lang: i18n.locale } });
      }
    }
  }

  // At this point, redirection is possible. We must ensure that the correct share class is selected
  // so that the left navigation bar opens correctly if needed. Without this, if a user accesses a
  // product page directly via the URL, the left navigation bar will not open as it normally does
  // during navigation.
  if (to.meta != undefined && to.meta.productId != undefined) {
    const product = config.products.find((product) => product.productId == to.meta.productId);
    if (product != undefined && product.productShareClassRoot != undefined) {
      store.dispatch("allProducts/setProductIdToShowForShareClassRoot", {
        shareClassRoot: product.productShareClassRoot,
        productId: product.productId,
      });
    }
  }

  // For product pages, verify access permissions for private content.
  if (to.meta != undefined && to.meta.productId != undefined) {
    const product = config.products.find((product) => product.productId == to.meta.productId);
    await store.dispatch(`${product.storeModule}/bindProductConfig`, {
      productId: product.productId,
    });
    const productVisibility = store.getters[`${product.storeModule}/getProductVisibility`];
    // Deny access if visibility is unknown (fail-safe) or if the product is private and the user
    // lacks viewing permissions.
    if (
      productVisibility == undefined ||
      (productVisibility === ProductVisibility.private &&
        !store.getters["userInfo/isViewerOf"](product.productId))
    ) {
      return next({ name: "page-error-403", params: { lang: i18n.locale } });
    }
  }

  // In case we don't want to be bothered by the investor profile at all
  // we can bypass the rest of the verifications.
  if (
    !store.getters["platformSettings/getCompliance"](i18n.locale)?.[
      "investorProfileChecksActivated"
    ]
  ) {
    return next();
  }

  // Now that the roles are checked, we can see if we have investor profile
  // restrictions. If not, we can simply return the page the user wanted
  // to access. Note that if we are part of the team, we should be able to not care
  // about investor profile.
  if (
    to.meta.noNeedOfInvestorProfile ||
    store.getters["userInfo/hasAnyRoles"]([Role.superAdmin, Role.admin, Role.team])
  ) {
    return next();
  }

  // We require to have the investor profile info (type and country)
  // of a user, so if we are missing those information, we request them.
  const userInfo = store.getters["userInfo/getUserInfo"];
  const investorProfileType =
    (userInfo && userInfo.investorProfile) || store.getters["userInfo/getIpTypeBrowserStored"];
  const investorProfileCountry =
    (userInfo && userInfo.country) || store.getters["userInfo/getIpCountryBrowserStored"];
  if (!investorProfileType || !investorProfileCountry) {
    // We need to get the investor profile (we don't have it), so go to
    // the page where the user has to choose it, and add the page we wanted to
    // go to as the "redirect" parameter to remember where we wanted to go.
    // Also don't forget to keep the current query parameters and the "params".
    const query = to.query;
    const params = to.params;
    params.lang = i18n.locale;
    query["redirect"] = to.path;
    return next({
      name: "page-investor-profile",
      query: query,
      params: params,
    });
  }

  // Now that we are sure to have the investor profile info, we can check
  // if we should redirect the user to the unauthorized investor profile page.
  const isProductPage = to.meta && to.meta.productId;
  if (isProductPage) {
    // First check if the user cannot access this product because of their role.
    // Go to 403 page if that's the case.
    const authorizedProductsForRole = store.getters["allProducts/getAllProductsAuthorizedForRole"];
    const hasAccessFromRole = authorizedProductsForRole
      .map((product) => product.productId)
      .includes(to.meta.productId);
    if (!hasAccessFromRole) {
      return next({
        name: "page-error-403",
        params: { lang: i18n.locale },
      });
    }

    // Then, check if the user cannot access this product because of their investor profile.
    // Go to the 'unauthorized page' if that's the case.
    const authorizedProductsForInvestorProfile =
      store.getters["allProducts/getAllProductsAuthorizedForInvestorProfile"];
    const hasAccessFromInvestorProfile = authorizedProductsForInvestorProfile
      .map((product) => product.productId)
      .includes(to.meta.productId);
    if (!hasAccessFromInvestorProfile) {
      return next({
        name: "page-unauthorized-investor-profile",
        params: {
          productId: to.meta.productId,
          unauthorizedPath: to.path,
          lang: i18n.locale,
        },
        query: to.query,
      });
    }
  } else {
    // If this is not a product page, we would like to verify if the user has access to at
    // least one other product, otherwise we would also redirect to the unauthorized investor
    // profile page.

    // First check if the user cannot access any product because of their role.
    // Go to 403 page if that's the case.
    const authorizedProductsForRole = store.getters["allProducts/getAllProductsAuthorizedForRole"];
    if (authorizedProductsForRole.length === 0) {
      return next({
        name: "page-error-403",
        params: {
          lang: i18n.locale,
        },
      });
    }

    // Then, check if the user cannot access any product because of their investor profile.
    // Go to the 'unauthorized page' if that's the case.
    const authorizedProductsForInvestorProfile =
      store.getters["allProducts/getAllProductsAuthorizedForInvestorProfile"];
    if (authorizedProductsForInvestorProfile.length == 0) {
      return next({
        name: "page-unauthorized-investor-profile",
        params: {
          unauthorizedPath: to.path,
          lang: i18n.locale,
        },
        query: to.query,
      });
    }
  }

  // If we reach this point, the role and investor profile restrictions are ok.
  next();
});

export default router;
